import React, { useState, useRef, useEffect, useCallback } from 'react';
import { UserAvatar } from './UserAvatar';
import { BotAvatar } from './BotAvatar';
import { AnimatedGradientBar } from './AnimatedGradientBar';
import { RecommendationSummary } from './RecommendationSummary';
import { formatText } from './formatText';
import { LoadingIndicator } from './LoadingIndicator';
import { useSelector } from "react-redux";

const FormattedMessage = ({ message }) => {
  const formattedMessage = formatText(message);

  return (
    <div
      className="flex-1 whitespace-pre-wrap"
      dangerouslySetInnerHTML={{ __html: formattedMessage }}
    />
  );
};

const ChatBubble = ({
  message,
  isUser,
  logoSrc,
  logoError,
  userImage,
  overrideDetails,
  onAcceptOverride,
  isAccepting
}) => {
  const handleAccept = () => {
    if (overrideDetails) {
      const subtype = overrideDetails.override_type || overrideDetails.override_subtype || null;
      // Transport service override
      if (overrideDetails.transport_service) {
        onAcceptOverride(overrideDetails.transport_service, 'transport', subtype);
      }
      // Supplier override (string supplier name)
      else if (overrideDetails.supplier_name || (overrideDetails.custom_name && subtype === 'supplier')) {
        const supplier = overrideDetails.supplier_name || overrideDetails.custom_name;
        onAcceptOverride(supplier, 'supplier', subtype);
      }
      // Movement quantity override (numeric)
      else if (overrideDetails.override_qty !== undefined) {
        onAcceptOverride(overrideDetails.override_qty, 'movement', subtype);
      }
      // Price override
      else if (overrideDetails.override_price !== undefined) {
        onAcceptOverride(overrideDetails.override_price, 'price', subtype);
      }
    }
  };

  return (
    <div className="mb-3 flex justify-center">
      <div
        className={`flex gap-3 p-3 rounded-lg border break-words text-base
          ${isUser ? 'bg-white border-gray-200 text-gray-900' : 'border-gray-300 text-gray-900'}`}
        style={{
          width: '527px',
          backgroundColor: isUser ? '#ffffff' : '#F1F5F9'
        }}
      >
        <div className="shrink-0">
          {isUser ? <UserAvatar userImage={userImage} /> : <BotAvatar logoSrc={logoSrc} hasError={logoError} />}
        </div>
        <div className="flex flex-col w-full">
          <FormattedMessage message={message} />
          {!isUser && overrideDetails && (
            <div className="mt-2">
              <button
                onClick={handleAccept}
                disabled={isAccepting}
                className={`text-black underline hover:text-gray-800 focus:outline-none ${isAccepting ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {isAccepting ? 'Accepting...' : 'Accept'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const ChatContent = ({
  messages,
  logoSrc,
  logoError,
  userImage,
  isLoading,
  onAcceptOverride,
  isAccepting
}) => {
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  return (
    <div className="flex-1 overflow-y-auto px-4 py-4 bg-transparent">
      <>
        {messages.map((msg, idx) => (
          <ChatBubble
            key={`${idx}-${msg.timestamp || idx}`}
            message={msg.text}
            isUser={msg.isUser}
            logoSrc={logoSrc}
            logoError={logoError}
            userImage={userImage}
            overrideDetails={msg.overrideDetails}
            onAcceptOverride={onAcceptOverride}
            isAccepting={isAccepting && !msg.isUser}
          />
        ))}
        {isLoading && <LoadingIndicator />}
      </>
      <div ref={bottomRef} />
    </div>
  );
};

// Helper function to determine recommendation type from ID
const getRecommendationType = (recommendationId) => {
  if (!recommendationId) return 'movement';
  if (Array.isArray(recommendationId)) {
    const first = recommendationId.find(id => typeof id === 'string');
    if (!first) return 'movement';
    recommendationId = first;
  }
  if (typeof recommendationId !== 'string') return 'movement';

  if (recommendationId.includes('price_rec') || recommendationId.startsWith('price_')) {
    return 'price';
  }
  return 'movement';
};

const sendToExplanationAPI = async (recommendationId, userQuery, chatHistory = [], userEmail = "monica@kimaru.ai", containerId = null) => {
  try {
    // If recommendationId is an array, group by type and call the plural endpoints for each group
    if (Array.isArray(recommendationId)) {
      // POST array of IDs to explain-recommendations endpoint
      const endpoint = "https://api.stag.kimaru.ai/api/v1/movement_recommendation/explain-recommendations";
      const payload = {
        recommendation_id: recommendationId,
        user_query: userQuery,
        chat_history: chatHistory
      };

      const resp = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Accept": "application/json", "user-email": userEmail },
        body: JSON.stringify(payload)
      });

      if (!resp.ok) {
        const txt = await resp.text().catch(() => null);
        console.error(`HTTP error for ${endpoint}: ${resp.status}`, txt);
        return { message: `HTTP ${resp.status}: ${txt || 'Failed to fetch explanations'}`, overrideDetails: null };
      }

      const data = await resp.json().catch(() => null);
      if (data && data.chat_history && Array.isArray(data.chat_history)) {
        const modelResponses = data.chat_history.filter(item => item.role === 'model');
        if (modelResponses.length > 0) {
          const lastParts = modelResponses[modelResponses.length - 1].parts;
          if (typeof lastParts === 'string') return { message: lastParts.split('<@CustomAcceptButton>')[0].trim(), overrideDetails: null };
          if (typeof lastParts === 'object' && lastParts.message) return { message: lastParts.message.split('<@CustomAcceptButton>')[0].trim(), overrideDetails: null };
        }
      }
      return { message: 'No model responses found for the selected recommendations', overrideDetails: null };
    }
    // If no recommendationId provided but containerId is, call the container-level explain endpoint
    if (!recommendationId && containerId) {
      const endpoint = "https://api.stag.kimaru.ai/api/v1/movement_recommendation/explain-recommendations";
      const payload = {
        container_id: containerId,
        user_query: userQuery,
        chat_history: chatHistory
      };

      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "user-email": userEmail
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`HTTP error! status: ${response.status}, response: ${errorText}`);
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const responseData = await response.json();
      if (responseData.chat_history && Array.isArray(responseData.chat_history)) {
        const modelResponses = responseData.chat_history.filter(item => item.role === 'model');
        if (modelResponses.length > 0) {
          const lastResponse = modelResponses[modelResponses.length - 1].parts;
          if (typeof lastResponse === 'string') {
            return { message: lastResponse.split('<@CustomAcceptButton>')[0].trim(), overrideDetails: null };
          }
          if (typeof lastResponse === 'object' && lastResponse.message) {
            return { message: lastResponse.message.split('<@CustomAcceptButton>')[0].trim(), overrideDetails: null };
          }
        }
        return { message: "No model response found in chat history", overrideDetails: null };
      }
      return { message: "Unexpected response format from API", overrideDetails: null };
    }

    const recommendationType = getRecommendationType(recommendationId);
    const endpoint = recommendationType === 'price'
      ? "https://api.stag.kimaru.ai/api/v1/price_recommendation/explain_recommendation"
      : "https://api.stag.kimaru.ai/api/v1/movement_recommendation/explain_recommendation";

    const payload = {
      recommendation_id: recommendationId,
      user_query: userQuery,
      chat_history: chatHistory
    };

    const response = await fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
        "user-email": userEmail
      },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`HTTP error! status: ${response.status}, response: ${errorText}`);
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const responseData = await response.json();
    if (responseData.chat_history && Array.isArray(responseData.chat_history)) {
      const modelResponses = responseData.chat_history.filter(item => item.role === 'model');
      if (modelResponses.length > 0) {
        const lastResponse = modelResponses[modelResponses.length - 1].parts;

        // Handle both string and object responses
        if (typeof lastResponse === 'string') {
          const cleanMessage = lastResponse.split('<@CustomAcceptButton>')[0].trim();

          let overrideDetails = null;
          if (lastResponse.includes('<@CustomAcceptButton>')) {
            if (recommendationType === 'price') {
              // Updated regex to match "changed from X YEN to Y YEN" pattern
              const priceMatch = lastResponse.match(/to\s+(\d+(?:\.\d+)?)\s+YEN\s+instead/i) ||
                lastResponse.match(/Recommended:\s*¥(\d+)/i) ||
                lastResponse.match(/¥(\d+).*profit.*←.*RECOMMENDED/i);
              if (priceMatch) {
                overrideDetails = { override_price: parseInt(priceMatch[1]) };
              }
            } else {
              const qtyMatch = lastResponse.match(/(\d+)\s*units?/i);
              if (qtyMatch) {
                overrideDetails = { override_qty: parseInt(qtyMatch[1]) };
              }
            }
          }

          return { message: cleanMessage, overrideDetails };
        }
        else if (typeof lastResponse === 'object' && lastResponse.message) {
          const cleanMessage = lastResponse.message.split('<@CustomAcceptButton>')[0].trim();
          const overrideDetails = {};

          if (lastResponse.override_qty !== undefined) {
            overrideDetails.override_qty = lastResponse.override_qty;
          }
          if (lastResponse.override_price !== undefined) {
            overrideDetails.override_price = lastResponse.override_price;
          }
          // Capture any override_type/custom_name provided by backend (could be 'transport', 'quantity', etc.)
          if (lastResponse.override_type) {
            overrideDetails.override_type = lastResponse.override_type;
          }
          if (lastResponse.custom_name) {
            overrideDetails.custom_name = lastResponse.custom_name;
          }
          // Support supplier_name returned by backend (e.g. for supplier overrides)
          if (lastResponse.supplier_name) {
            overrideDetails.supplier_name = lastResponse.supplier_name;
            overrideDetails.custom_name = lastResponse.supplier_name;
            overrideDetails.override_type = overrideDetails.override_type || 'supplier';
          }
          // Handle transport override (backward-compatible: transport_service field)
          if ((lastResponse.override_type === 'transport' && lastResponse.transport_service) || lastResponse.transport_service) {
            overrideDetails.transport_service = lastResponse.transport_service || lastResponse.custom_name;
            overrideDetails.override_type = 'transport';
          }

          return {
            message: cleanMessage,
            overrideDetails: Object.keys(overrideDetails).length > 0 ? overrideDetails : null
          };
        }
      }
      return { message: "No model response found in chat history", overrideDetails: null };
    }

    return { message: "Unexpected response format from API", overrideDetails: null };
  } catch (err) {
    console.error("API error:", err);
    return { message: "An Error Occurred. Please try again a little later", overrideDetails: null };
  }
};

const sendOverrideAPI = async (recommendationId, overrideValue, overrideType, userEmail = "monica@kimaru.ai", overrideSubtype = null) => {
  try {
    let endpoint;
    let payload;

    if (overrideType === 'price') {
      endpoint = "https://api.stag.kimaru.ai/api/v1/price_recommendation/override_recommendation";
      payload = { recommendation_id: recommendationId, override_price: overrideValue };
    } else if (overrideType === 'transport') {
      endpoint = "https://api.stag.kimaru.ai/api/v1/movement_recommendation/override_recommendation";
      // Backend expects override_type to be the type (e.g. 'transport') and the service name in custom_name
      payload = { recommendation_id: recommendationId, override_type: 'transport', custom_name: overrideValue, override_quantity: 0 };
    } else if (overrideType === 'supplier') {
      // Supplier overrides are submitted to the movement override endpoint with override_type 'supplier'
      endpoint = "https://api.stag.kimaru.ai/api/v1/movement_recommendation/override_recommendation";
      payload = { recommendation_id: recommendationId, override_type: 'supplier', custom_name: overrideValue, override_quantity: 0 };
    } else {
      endpoint = "https://api.stag.kimaru.ai/api/v1/movement_recommendation/override_recommendation";
      // For movement overrides, include override_quantity and put the provided value in custom_name (as string)
      // Do NOT set override_type here — backend expects override_type to be null for pure movement overrides
      payload = { recommendation_id: recommendationId, override_quantity: overrideValue, custom_name: String(overrideValue) };
      // if backend explicitly wants an override_type like 'quantity' include it
      if (overrideSubtype) {
        payload.override_type = overrideSubtype;
      }
    }

    const response = await fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
        "user-email": userEmail
      },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`Override API error! status: ${response.status}, response: ${errorText}`);
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (err) {
    console.error("Override API error:", err);
    throw err;
  }
};

// Fetch accepted recommendations from server and return the matching one by recommendation id
const getAcceptedRecommendation = async (recommendationId, userEmail, recType = 'movement') => {
  try {
    const endpoint = recType === 'price'
      ? 'https://api.stag.kimaru.ai/api/v1/price_recommendation/get_accepted_recommendations'
      : 'https://api.stag.kimaru.ai/api/v1/movement_recommendation/get_accepted_recommendations';

    const resp = await fetch(endpoint, {
      method: 'GET',
      headers: {
        accept: 'application/json',
        'user-email': userEmail
      }
    });

    if (!resp.ok) {
      console.warn('Failed to fetch accepted recommendations', resp.status);
      return null;
    }

    const data = await resp.json();
    // Expecting an array; find matching rec_id or recommendation_id
    if (Array.isArray(data)) {
      return data.find(item => item.rec_id === recommendationId || item.recommendation_id === recommendationId) || null;
    }

    return null;
  } catch (err) {
    console.error('Error fetching accepted recommendation', err);
    return null;
  }
};

const formatOverrideConfirmation = (responseData, overrideType, overrideValue, recommendation) => {
  if (overrideType === 'price') {
    const originalPrice = responseData.original_recommended_price;
    const newPrice = responseData.override_price;
    const adjustment = responseData.adjustment_value;
    const adjustmentText = adjustment >= 0 ? `+¥${adjustment}` : `¥${adjustment}`;

    return `Override Accepted: Price updated from ¥${originalPrice} to ¥${newPrice} (${adjustmentText})`;
  } else if (overrideType === 'transport') {
    return `Override Accepted: Transport service changed to '${overrideValue}'`;
  } else {
    const originalDesc = recommendation?.recommendation?.description || '';
    const updatedDesc = originalDesc.replace(/\d+/, overrideValue.toString());
    return `Override Accepted: ${updatedDesc}`;
  }
};

const AgenticInteractionDrawer = ({
  open,
  onClose,
  logoPath,
  userImage,
  recommendationId,
  recommendation,
  recommendationType,
  containerId,
  agentScope, // optional: 'product' | 'container' | null
  onAcceptSuccess // callback when override accepted: (recommendationId) => void
}) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [logoError, setLogoError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isAccepting, setIsAccepting] = useState(false);
  const [updatedRecommendation, setUpdatedRecommendation] = useState(null);
  const logoSrc = logoPath || '/kimaru-favicon.png';

  // ✅ Moved useSelector here
  const userEmail = useSelector((state) => state.auth?.user?.email) || "monica@kimaru.ai";

  const currentRecommendationType = recommendationType || getRecommendationType(recommendationId || '');

  const handleSend = useCallback(async () => {
    if (!input.trim() || isLoading) return;

    const userMessage = {
      text: input.trim(),
      isUser: true,
      timestamp: Date.now(),
      overrideDetails: null
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      const chatHistory = messages.map((msg) => ({
        role: msg.isUser ? 'user' : 'model',
        parts: msg.text
      }));

      const botReply = await sendToExplanationAPI(
        recommendationId,
        userMessage.text,
        chatHistory,
        userEmail, // ✅ pass here
        containerId
      );

      setMessages(prev => [...prev, {
        text: botReply.message,
        isUser: false,
        timestamp: Date.now(),
        overrideDetails: botReply.overrideDetails
      }]);
    } catch (error) {
      setMessages(prev => [...prev, {
        text: "An Error Occurred. Please try again a little later",
        isUser: false,
        timestamp: Date.now(),
        overrideDetails: null
      }]);
    } finally {
      setIsLoading(false);
    }
  }, [input, isLoading, messages, recommendationId, userEmail]);

  const handleAcceptOverride = useCallback(async (overrideValue, overrideType, overrideSubtype = null) => {
    setIsAccepting(true);

    try {
      const responseData = await sendOverrideAPI(recommendationId, overrideValue, overrideType, userEmail, overrideSubtype);
      const confirmationMessage = formatOverrideConfirmation(responseData, overrideType, overrideValue, recommendation);

      // Try to fetch the server-side accepted recommendation and use it if available
      const accepted = await getAcceptedRecommendation(recommendationId, userEmail, currentRecommendationType);
      if (accepted) {
        setUpdatedRecommendation(prev => {
          const base = prev ? { ...prev } : { ...(recommendation || {}) };
          const target = base.recommendation ? { ...base.recommendation } : { ...base };

          // backend fields may be top-level names: description, custom_name, override_type, quantity_to_order, rec_id
          if (accepted.description) target.description = accepted.description;
          if (accepted.custom_name !== undefined) target.custom_name = accepted.custom_name;
          if (accepted.override_type !== undefined) target.override_type = accepted.override_type;
          if (accepted.quantity_to_order !== undefined) target.quantity_to_order = accepted.quantity_to_order;

          if (base.recommendation) return { ...base, recommendation: target };
          return target;
        });
      } else {
        // fallback: keep previous local derivation
        if (overrideType === 'transport') {
          const updatedDesc = `Expedite order by switching to ${overrideValue}.`;
          setUpdatedRecommendation(prev => {
            const updated = prev ? { ...prev } : { ...recommendation };
            if (updated.recommendation) {
              updated.recommendation.description = updatedDesc;
              updated.recommendation.custom_name = overrideValue;
              updated.recommendation.override_type = 'transport';
            } else {
              updated.description = updatedDesc;
              updated.custom_name = overrideValue;
              updated.override_type = 'transport';
            }
            return updated;
          });
        } else if (overrideType === 'price') {
          const updatedDesc = `Price override to ¥${overrideValue}`;
          setUpdatedRecommendation(prev => {
            const updated = prev ? { ...prev } : { ...recommendation };
            if (updated.recommendation) {
              updated.recommendation.description = updatedDesc;
              updated.recommendation.override_type = 'price';
              updated.recommendation.custom_name = null;
            } else {
              updated.description = updatedDesc;
              updated.override_type = 'price';
              updated.custom_name = null;
            }
            return updated;
          });
        } else if (overrideType === 'movement') {
          const updatedDesc = `Reorder ${overrideValue} units to address deficit and stockout risk`;
          setUpdatedRecommendation(prev => {
            const updated = prev ? { ...prev } : { ...recommendation };
            if (updated.recommendation) {
              updated.recommendation.description = updatedDesc;
              updated.recommendation.custom_name = overrideValue;
              updated.recommendation.override_type = null;
              updated.recommendation.quantity_to_order = overrideValue;
            } else {
              updated.description = updatedDesc;
              updated.custom_name = overrideValue;
              updated.override_type = null;
              updated.quantity_to_order = overrideValue;
            }
            return updated;
          });
        }
      }

      setMessages(prev => [...prev, {
        text: confirmationMessage,
        isUser: false,
        timestamp: Date.now(),
        overrideDetails: null
      }]);
      // Notify parent that the recommendation(s) were accepted/overridden
      try {
        if (typeof onAcceptSuccess === 'function') {
          onAcceptSuccess(recommendationId);
        }
      } catch (err) {
        console.warn('onAcceptSuccess callback failed', err);
      }
    } catch (error) {
      setMessages(prev => [...prev, {
        text: "⚠️ Failed to accept override. Please try again.",
        isUser: false,
        timestamp: Date.now(),
        overrideDetails: null
      }]);
    } finally {
      setIsAccepting(false);
    }
  }, [recommendationId, recommendation, userEmail]);

  const handleInputKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }, [handleSend]);

  useEffect(() => {
    if (!open) {
      setMessages([]);
      setInput('');
      setIsLoading(false);
      setIsAccepting(false);
      setUpdatedRecommendation(null);
    }
  }, [open]);

  // Reset updatedRecommendation when recommendationId changes
  useEffect(() => {
    setUpdatedRecommendation(null);
  }, [recommendationId]);

  if (!open) return null;

  // Determine drawer title by explicit agentScope first, then fall back to recommendation type
  let drawerTitle;
  if (userEmail?.trim()?.toLowerCase() === "sam@kimaru.ai") {
    drawerTitle = 'kimaru.ai Agent Interaction - Supplier Order Recommendation';
  } else if (agentScope === 'product') {
    drawerTitle = 'kimaru.ai Agent Interaction - Product Recommendations';
  } else if (agentScope === 'container') {
    drawerTitle = 'kimaru.ai Agent Interaction - Container Recommendations';
  } else if (currentRecommendationType === 'price') {
    drawerTitle = 'kimaru.ai Agent Interaction - Price Recommendation';
  } else {
    drawerTitle = 'kimaru.ai Agent Interaction - Movement Recommendation';
  }

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div
        className="pointer-events-none fixed left-0 bottom-0 z-50"
        style={{ width: '32px', height: '100%', boxShadow: '-4px 0px 15px 0px rgba(82, 21, 163, 0.25)', borderRadius: '0 12px 12px 0' }}
      />
      <div
        className="absolute inset-0"
        style={{ background: 'rgba(255,255,255,0.7)', backdropFilter: 'blur(2px)' }}
        onClick={onClose}
      />

      <div
        className="relative w-full sm:w-4/5 md:w-3/5 lg:w-2/5 h-full flex"
        style={{ background: '#F8FAFC', boxShadow: '-4px 0px 15px 0px rgba(82, 21, 163, 0.25)' }}
      >
        <AnimatedGradientBar isThinking={isLoading || isAccepting} />

        <div className="flex flex-col h-full w-full pt-4 px-4 z-20">
          <div className="px-4 pb-3 flex items-center min-h-[42px]">
            <h2 className="text-lg font-semibold text-gray-900 leading-tight">{drawerTitle}</h2>
            <button
              onClick={onClose}
              className="ml-auto"
              style={{
                width: 'var(--width-w-4, 16px)',
                height: 'var(--height-h-4, 16px)',
                flexShrink: 0,
                background: 'none',
                border: 'none',
                cursor: 'pointer'
              }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M12 4L4 12M4 4L12 12" stroke="#111827" strokeWidth="1.33" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </div>

          {recommendation && <RecommendationSummary recommendation={updatedRecommendation || recommendation} />}

          <ChatContent
            messages={messages}
            logoSrc={logoSrc}
            logoError={logoError}
            userImage={userImage}
            isLoading={isLoading}
            onAcceptOverride={handleAcceptOverride}
            isAccepting={isAccepting}
          />

          <div className="px-4 pt-3 pb-2 bg-transparent border-t border-gray-100">
            <div className="flex items-center gap-2">
              <div className="flex-1 relative">
                <textarea
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg resize-none focus:outline-none focus:border-violet-500 focus:ring-1 focus:ring-violet-500 text-sm bg-white"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleInputKeyDown}
                  disabled={isLoading || isAccepting}
                  rows={2}
                  style={{ minHeight: '48px', maxHeight: '96px' }}
                  placeholder="Start typing a question or explain what you would like kimaru.ai to do..."
                />
                <button
                  onClick={handleSend}
                  disabled={!input.trim() || isLoading || isAccepting}
                  className="absolute top-1/2 right-2 transform -translate-y-1/2 p-2 rounded-full"
                  style={{
                    borderRadius: 'var(--Corner-Large, 16px)',
                    background: 'var(--base-primary, #0F172A)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    cursor: 'pointer',
                    opacity: !input.trim() || isLoading || isAccepting ? 0.5 : 1
                  }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M3.33331 7.99967H12.6666M12.6666 7.99967L7.99998 3.33301M12.6666 7.99967L7.99998 12.6663" stroke="#F8FAFC" strokeWidth="1.33" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
              </div>
            </div>

          </div>
        </div>

        {logoSrc && !logoError && (
          <img
            src={logoSrc}
            alt=""
            style={{ display: 'none' }}
            onError={() => setLogoError(true)}
            onLoad={() => setLogoError(false)}
          />
        )}
      </div>
    </div>
  );
};

export default AgenticInteractionDrawer;
